import { Trans, useTranslation } from "react-i18next";

import { Link } from "@aviary";
import { WarningBar } from "@clinic/shared/components/WarningBar/WarningBar";
import { clinicRoutes } from "@clinic/shared/data/clinicRoutes";
import { l } from "@clinic/shared/locales/i18n";
import { useCertifications } from "@shared/hooks/useCertifications/useCertifications";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { StorePlatform } from "@shared/types/StorePlatform";

import * as styles from "../WarningBars.styles";

interface Props {
  isStoreComplete: boolean;
  isGradDateInFuture: boolean;
  storePlatform: StorePlatform;
}

const StudentCredentialsWarningBar = ({
  isStoreComplete,
  isGradDateInFuture,
  storePlatform,
}: Props) => {
  const { t } = useTranslation();
  const [studentWorkflow] = useFlippers("student_workflow");
  const { isCertified, hasSubmittedCertifications } = useCertifications();

  const isEmerson = storePlatform === StorePlatform.EMERSON;

  const renderDoesNotHaveCredentials = () => {
    if (!studentWorkflow) {
      return null;
    }

    switch (storePlatform) {
      case StorePlatform.FULLSCRIPT:
        return (
          <Trans i18nKey={l.sharedWarningBars.credentials.hasNoCredentials}>
            <strong>Credentials required.</strong> Orders through your dispensary for friends and
            family will not be shipped until you upload your credentials and we approve it.
            <Link isLight href={clinicRoutes.accountSettings}>
              Upload credentials &raquo;
            </Link>
          </Trans>
        );
      case StorePlatform.EMERSON:
        return t(l.sharedWarningBars.certification.emerson.hasNoCertifications);
    }
  };

  const renderHasCredentials = () => {
    switch (storePlatform) {
      case StorePlatform.FULLSCRIPT:
        return (
          <Trans i18nKey={l.sharedWarningBars.credentials.hasCredentials}>
            <strong>Your credentials are pending review by our team.</strong> Orders through your
            dispensary will be shipped once we approve your credentials.
          </Trans>
        );
      case StorePlatform.EMERSON:
        return t(l.sharedWarningBars.certification.emerson.hasCertifications);
    }
  };

  const renderCongratsOnGraduating = () => (
    <Trans i18nKey={l.sharedWarningBars.credentials.congratsOnGraduating}>
      <strong>Congratulations on graduating!</strong> Please upload your new certification.
      <Link isLight to={clinicRoutes.accountSettings}>
        Upload certification &raquo;
      </Link>
    </Trans>
  );

  const renderStudentCredentialsBanner = () => {
    if (!isGradDateInFuture && !isEmerson) return renderCongratsOnGraduating();

    return hasSubmittedCertifications ? renderHasCredentials() : renderDoesNotHaveCredentials();
  };

  const storeInvalidForBanner = () => {
    if (!isStoreComplete) return true;

    return isCertified && isGradDateInFuture;
  };

  const renderCondition = () => {
    if (storeInvalidForBanner()) return null;

    return renderStudentCredentialsBanner();
  };

  const condition = renderCondition();
  if (condition === null) {
    return null;
  }
  const barType = hasSubmittedCertifications ? "primary" : "danger";

  return (
    <WarningBar
      type={barType}
      autoHeight={storePlatform === StorePlatform.EMERSON}
      isFixed={false}
      css={!isEmerson && styles.warningBar}
    >
      {condition}
    </WarningBar>
  );
};

export { StudentCredentialsWarningBar };
